import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  inject,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, switchMap } from 'rxjs';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { StatusFilterComponent } from '@shared/components/status-filter/status-filter.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { VirtualWarehouseService } from '../services/virtual-warehouse.service';
import {
  VirtualWarehouse,
  VirtualWarehouseSearchParams,
  VirtualWarehouseUpdateRequest,
} from '../models/virtual-warehouse.model';
import {
  VietnamAddressService,
  VietnamProvince,
} from '../../services/vietnam-address.service';
import { PermissionService } from '@app/core/services/permission.service';
import {
  VIRTUAL_WAREHOUSE_SEARCH_CONFIG,
  VIRTUAL_WAREHOUSE_COLUMNS,
  VIRTUAL_WAREHOUSE_DEFAULT_COLUMNS_ORDER,
  VIRTUAL_WAREHOUSE_FLEXIBLE_COLUMNS_ORDER,
  VIRTUAL_WAREHOUSE_ACTION_COLUMN,
} from '../virtual-warehouse.config';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import {
  buildWarehouseDefaultColumns,
  buildWarehouseFlexibleColumns,
} from '../../utils/warehouse-table-columns.util';
import {
  buildWarehouseDeleteDialogState,
  buildWarehouseStatusChangeDialogState,
} from '../../utils/warehouse-confirmation-dialog.util';
import {
  WAREHOUSE_LIST_STATUS_TAG_STYLE,
  warehouseListStatusDisplayText,
  warehouseListStatusSeverity,
} from '../../utils/warehouse-status-tag.util';
import { translateWarehouseProvinceCityName } from '../../utils/warehouse-province-display.util';
import { formatMemberWarehouseLabels } from '../../utils/warehouse-member-format.util';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import { openWarehouseTableColumnSelector } from '../../utils/warehouse-column-selector.util';
import {
  mergeVirtualWarehouseListSearch,
  resetVirtualWarehouseListSearch,
  virtualWarehouseListHasActiveFilters,
} from '../../utils/warehouse-virtual-list-search.util';

@Component({
  selector: 'app-virtual-warehouse-list',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './virtual-warehouse-list.component.html',
  styleUrl: './virtual-warehouse-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualWarehouseListComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly virtualWarehouseService = inject(VirtualWarehouseService);
  private readonly translateService = inject(TranslateService);
  private readonly vietnamAddressService = inject(VietnamAddressService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly destroy$ = new Subject<void>();

  // Permission flags - computed from PermissionService
  // Using specific submenu 'virtual_warehouse' for warehouse permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.virtual_warehouse.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.virtual_warehouse.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.virtual_warehouse.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.virtual_warehouse.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.virtual_warehouse.approve');
  });

  // Provinces data for translation
  provinces = signal<VietnamProvince[]>([]);

  // Current language - track via signal
  private currentLang = signal<string>('vi');

  searchConfig = computed(() => {
    const lang = this.currentLang();
    return withPaginatedSearchFields([...VIRTUAL_WAREHOUSE_SEARCH_CONFIG], {
      cityCode: {
        loadPage: (params) =>
          this.vietnamAddressService.getProvinceOptionsPage(params, lang),
      },
    });
  });

  // Check if user has any action permissions (UPDATE, APPROVE, or DELETE)
  hasAnyActionPermission = computed(
    () => this.canUpdate() || this.canApprove() || this.canDelete(),
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] =>
    buildWarehouseDefaultColumns(
      VIRTUAL_WAREHOUSE_COLUMNS,
      VIRTUAL_WAREHOUSE_DEFAULT_COLUMNS_ORDER,
      VIRTUAL_WAREHOUSE_ACTION_COLUMN.ACTIONS,
      this.hasAnyActionPermission(),
    ),
  );

  flexibleColumns = computed((): TableColumn[] =>
    buildWarehouseFlexibleColumns(
      VIRTUAL_WAREHOUSE_COLUMNS,
      VIRTUAL_WAREHOUSE_FLEXIBLE_COLUMNS_ORDER,
    ),
  );

  // Data signals
  warehouses = signal<VirtualWarehouse[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal<VirtualWarehouseSearchParams>({
    name: undefined,
    status: undefined,
    cityName: undefined,
    cityCode: undefined,
    page: 0,
    size: 20,
  });

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedWarehouseForAction: VirtualWarehouse | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'delete' | 'statusChange' | null = null;

  // Computed values
  warehousesData = computed(() => this.warehouses());

  ngOnInit(): void {
    // Initialize language
    this.currentLang.set(
      this.translateService.defaultLang ||
        this.translateService.getDefaultLang() ||
        'vi',
    );

    this.loadProvinces();

    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.currentLang.set(event.lang || 'vi');
      });

    this.loadWarehouses();
  }

  private loadProvinces(): void {
    this.vietnamAddressService
      .loadProvinces()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (provinces) => {
          this.provinces.set(provinces);
        },
        error: (error) => {
          console.error('Error loading provinces:', error);
        },
      });
  }

  getTranslatedCityName(cityName: string | undefined): string {
    return translateWarehouseProvinceCityName(
      cityName,
      this.provinces(),
      this.currentLang(),
    );
  }

  formatMemberWarehouses(memberWarehouseNames: string[] | undefined): string {
    return formatMemberWarehouseLabels(memberWarehouseNames);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Primary button configuration - computed with permissions
  primaryButton = computed(() => ({
    label: this.translateService.instant('common.actions.new'),
    severity: 'info' as const,
    outlined: false,
    show: this.canCreate(),
    icon: 'add',
  }));

  onSearch(params: { name?: string; cityCode?: unknown }): void {
    this.searchParams.set(
      mergeVirtualWarehouseListSearch(this.searchParams(), params),
    );
    this.loadWarehouses();
  }

  onReset(): void {
    const currentParams = this.searchParams();
    if (!virtualWarehouseListHasActiveFilters(currentParams)) {
      return;
    }
    this.searchParams.set({
      ...this.searchParams(),
      ...resetVirtualWarehouseListSearch(20),
    });
    this.loadWarehouses();
  }

  onAddWarehouse(): void {
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/warehouse/virtual/add']);
  }

  onEdit(warehouse: VirtualWarehouse): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/warehouse/virtual/edit', warehouse.id]);
  }

  onStatusChange(warehouse: VirtualWarehouse): void {
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    const newStatus = !warehouse.status;
    this.selectedWarehouseForAction = warehouse;
    const dlg = buildWarehouseStatusChangeDialogState(
      (k, p) => this.translateService.instant(k, p),
      'virtual_warehouse',
      warehouse.name,
      newStatus,
      false,
    );
    this.dialogTitle = dlg.title;
    this.dialogMessage = dlg.message;
    this.dialogButtons = dlg.buttons;
    this.currentAction = 'statusChange';
    this.showConfirmationDialog = true;
  }

  onDelete(warehouse: VirtualWarehouse): void {
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.selectedWarehouseForAction = warehouse;
    const dlg = buildWarehouseDeleteDialogState(
      (k, p) => this.translateService.instant(k, p),
      'virtual_warehouse',
      warehouse.name,
    );
    this.dialogTitle = dlg.title;
    this.dialogMessage = dlg.message;
    this.dialogButtons = dlg.buttons;
    this.currentAction = 'delete';
    this.showConfirmationDialog = true;
  }

  onConfirmatiButtonClick(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
    } else if (action === 'confirm') {
      if (this.currentAction === 'delete') {
        this.confirmDelete();
      } else if (this.currentAction === 'statusChange') {
        this.confirmStatusChange();
      }
    }
  }

  confirmDelete(): void {
    if (this.selectedWarehouseForAction) {
      this.deleteWarehouse();
    }
    this.closeConfirmationDialog();
  }

  confirmStatusChange(): void {
    if (this.selectedWarehouseForAction) {
      this.changeWarehouseStatus();
    }
    this.closeConfirmationDialog();
  }

  closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedWarehouseForAction = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  private changeWarehouseStatus(): void {
    if (!this.selectedWarehouseForAction?.id) {
      return;
    }

    const newStatus = !this.selectedWarehouseForAction.status;

    this.virtualWarehouseService
      .getVirtualWarehouseById(this.selectedWarehouseForAction.id)
      .pipe(
        switchMap((fullWarehouse) => {
          const updateData: VirtualWarehouseUpdateRequest = {
            name: fullWarehouse.name,
            status: newStatus,
            memberWarehouseIds: fullWarehouse.memberWarehouseIds,
          };

          return this.virtualWarehouseService.updateVirtualWarehouse(
            fullWarehouse.id!,
            updateData,
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant(
              'virtual_warehouse.messages.success.title',
            ),
            detail: this.translateService.instant(
              `virtual_warehouse.messages.success.warehouse_${
                newStatus ? 'activated' : 'deactivated'
              }`,
            ),
            life: 4000,
          });
          this.loadWarehouses();
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'virtual_warehouse.messages.error.title',
            ),
            detail: this.translateService.instant(
              'virtual_warehouse.messages.error.failed_to_update_status',
            ),
            life: 6000,
          });
        },
      });
  }

  private deleteWarehouse(): void {
    if (!this.selectedWarehouseForAction?.id) {
      return;
    }

    const warehouseId = this.selectedWarehouseForAction.id;

    this.virtualWarehouseService
      .deleteVirtualWarehouses([warehouseId])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant(
              'virtual_warehouse.messages.success.title',
            ),
            detail: this.translateService.instant(
              'virtual_warehouse.messages.success.warehouse_deleted',
            ),
            life: 4000,
          });
          this.loadWarehouses();
        },
        error: (error) => {
          console.error('Error deleting virtual warehouse:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'virtual_warehouse.messages.error.title',
            ),
            detail: this.translateService.instant(
              'virtual_warehouse.messages.error.failed_to_delete',
            ),
            life: 6000,
          });
        },
      });
  }

  onPageChange(event: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page, // Advanced table uses 0-based page
      size: event.rows,
    });
    this.loadWarehouses();
  }

  private loadWarehouses(): void {
    this.loading.set(true);

    this.virtualWarehouseService
      .getVirtualWarehouses(this.searchParams())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.warehouses.set(response.content || []);
          this.totalRecords.set(response.totalElements || 0);
          this.loading.set(false);
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'virtual_warehouse.messages.error.title',
            ),
            detail: this.translateService.instant(
              'virtual_warehouse.messages.error.failed_to_load',
            ),
          });
          this.loading.set(false);
        },
      });
  }

  getStatusDisplayText(status: boolean): string {
    return warehouseListStatusDisplayText(
      (k) => this.translateService.instant(k),
      'virtual_warehouse',
      status,
    );
  }

  getStatusSeverity(status: boolean): string {
    return warehouseListStatusSeverity(status);
  }

  getStatusStyle(_status: boolean): Record<string, string> {
    return { ...WAREHOUSE_LIST_STATUS_TAG_STYLE };
  }

  /**
   * Format datetime using utility function
   */
  formatDateTime = formatDateTime;

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    openWarehouseTableColumnSelector(this.advancedTable, event);
  }
}
